import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Slider,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import tempMetricsService from '../../../services/tempMetricsService';

const temperatureConfigs = [
  { value: 'cell_temp', label: 'Battery Cell Temperature' },
  { value: 'rectifier_temp', label: 'Rectifier Temperature' },
  { value: 'inverter_temp', label: 'Inverter Temperature' }  // Updated this entry
];

const yAxisConfig = {
  cell_temp: { label: 'Temperature (°C)', domain: [0, 60] },
  rectifier_temp: { label: 'Temperature (°C)', domain: [0, 60] },
  inverter_temp: { label: 'Temperature (°C)', domain: [0, 60] }  // Updated this entry
};

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const WINDOW_SIZES = {
  '1hour': 60 * 60 * 1000,
  '2hour': 2 * 60 * 60 * 1000,
  '3hour': 3 * 60 * 60 * 1000,
  '4hour': 4 * 60 * 60 * 1000,
  '5hour': 5 * 60 * 60 * 1000,
};

const menuItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  '& .MuiMenuItem-root': {
    borderBottom: '1px solid #eee',
    py: 1,  // Reduced vertical padding
    px: 0.5,  // Minimal horizontal padding
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  '& .MuiList-root': {
    padding: '2px 0',  // Reduced padding for the menu list container
  },
  '& .MuiPaper-root': {
    minWidth: 'auto'  // Allow paper to shrink to content
  }
};

const timeRangeMenuItemStyle = {
  my: 0.5,  // Add vertical margin between items
  minHeight: '32px',  // Reduced height
  display: 'flex',
  alignItems: 'center',
  px: 0.5,  // Reduced horizontal padding
  fontSize: '1rem'  // Default font size
};

const TemperatureMetrics = ({ emsName }) => {
  const [selectedMetric, setSelectedMetric] = useState('cell_temp');
  const [timeFilter, setTimeFilter] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [windowSize, setWindowSize] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(0);

  const fetchData = useCallback(async () => {
    if (!emsName) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await tempMetricsService.fetchTemperatureData(
        emsName,
        timeFilter,
        selectedDate
      );
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load temperature metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName, timeFilter, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleData = useMemo(() => {
    if (!data.length) return [];
    if (!windowSize) return data;
    
    try {
      // Debug raw data
      console.log('Raw processed data:', data[0]);

      // Convert timestamps to Date objects with validation
      const dataWithDates = data
        .map(item => {
          try {
            const timestamp = item.timestamp;
            let dateTime = null;

            if (typeof timestamp === 'string' && timestamp.includes('T')) {
              // ISO string format
              dateTime = new Date(timestamp);
            } else if (typeof timestamp === 'number' || !isNaN(parseInt(timestamp))) {
              // Unix timestamp (milliseconds)
              dateTime = new Date(parseInt(timestamp));
            }

            if (dateTime && !isNaN(dateTime.getTime())) {
              return {
                ...item,
                dateTime,
                originalTime: item.time
              };
            }
            return null;
          } catch (err) {
            console.error('Error parsing date for item:', item);
            return null;
          }
        })
        .filter(item => item !== null); // Remove items with invalid dates

      if (dataWithDates.length === 0) {
        console.error('No valid dates found in data. Sample data:', {
          first: data[0],
          total: data.length
        });
        return data;
      }

      // Sort by timestamp in ascending order
      const sortedData = [...dataWithDates].sort((a, b) => 
        a.dateTime.getTime() - b.dateTime.getTime()
      );

      // Calculate window indices
      const totalPoints = sortedData.length;
      const windowMs = WINDOW_SIZES[windowSize];
      const pointsPerWindow = Math.ceil(totalPoints * (windowMs / (24 * 60 * 60 * 1000)));
      const availablePositions = Math.max(0, totalPoints - pointsPerWindow);
      const startIndex = Math.floor(availablePositions * (sliderPosition / 100));
      const endIndex = Math.min(startIndex + pointsPerWindow, totalPoints);

      console.log('Window calculation:', {
        totalPoints,
        pointsPerWindow,
        startIndex,
        endIndex,
        windowMs,
        firstDate: sortedData[0]?.dateTime,
        lastDate: sortedData[sortedData.length - 1]?.dateTime
      });

      return sortedData
        .slice(startIndex, endIndex)
        .map(({ dateTime, ...rest }) => ({
          ...rest,
          time: dateTime.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
        }));

    } catch (error) {
      console.error('Error in visibleData calculation:', error);
      return data;
    }
  }, [data, windowSize, sliderPosition]);

  const handleMetricChange = useCallback((event) => {
    setSelectedMetric(event.target.value);
  }, []);

  const handleTimeFilterChange = useCallback((event) => {
    setTimeFilter(event.target.value);
    if (event.target.value !== 'daily') {
      setWindowSize(null);
    }
  }, []);

  const handleDateChange = useCallback((newDate) => {
    if (newDate && !isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  }, []);

  const handleWindowChange = useCallback((size) => {
    setWindowSize(size);
    setSliderPosition(0);
  }, []);

  const handleSliderChange = (event, newValue) => {
    setSliderPosition(newValue);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ minWidth: 200 }}>
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start',
        '& .MuiFormControl-root': {
          minWidth: 180
        }
      }}>
        <FormControl size="small">
          <InputLabel>Temperature Metric</InputLabel>
          <Select
            value={selectedMetric}
            label="Temperature Metric"
            onChange={handleMetricChange}
            MenuProps={{
              PaperProps: { sx: menuItemStyle },
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' }
            }}
          >
            {temperatureConfigs.map((metric) => (
              <MenuItem 
                key={metric.value} 
                value={metric.value}
                sx={timeRangeMenuItemStyle}
              >
                {metric.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeFilter}
            label="Time Range"
            onChange={handleTimeFilterChange}
            MenuProps={{
              PaperProps: { sx: menuItemStyle }
            }}
          >
            {timeFilters.map((filter) => (
              <MenuItem 
                key={filter.value} 
                value={filter.value}
                sx={timeRangeMenuItemStyle}
              >
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Box>

      {/* Chart Area */}
      <Box sx={{ mt: 2, mb: 3 }}>
        {data.length > 0 ? (
          <>
            {timeFilter === 'daily' && (
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                gap: 1,
                justifyContent: 'center',
                width: '100%'
              }}>
                <Button
                  variant={!windowSize ? 'contained' : 'outlined'}
                  onClick={() => handleWindowChange(null)}
                  size="small"
                >
                  View Full Day
                </Button>
                {Object.keys(WINDOW_SIZES).map((size) => (
                  <Button
                    key={size}
                    variant={windowSize === size ? 'contained' : 'outlined'}
                    onClick={() => handleWindowChange(size)}
                    size="small"
                    sx={{ minWidth: '80px' }}
                  >
                    {`${size.replace('hour', '')}h Window`}
                  </Button>
                ))}
              </Box>
            )}
            
            <Box sx={{ 
              position: 'relative', 
              overflowX: 'auto',
              maxWidth: '100%' 
            }}>
              <LineChart 
                width={900}
                height={300} 
                data={visibleData}
                margin={{ top: 5, right: 30, left: 50, bottom: 25 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tickFormatter={(unixTime) => {
                    const date = new Date(unixTime);
                    switch(timeFilter) {
                      case 'monthly':
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });
                      case 'weekly':
                        return `${date.toLocaleDateString()} ${date.getHours()}:00`;
                      case 'daily':
                        return date.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                      default:
                        return date.toLocaleString();
                    }
                  }}
                  interval={timeFilter === 'monthly' ? 'preserveStartEnd' : 
                           timeFilter === 'weekly' ? 12 : 
                           timeFilter === 'daily' ? 60 : 'preserveEnd'}
                  minTickGap={50}
                  ticks={timeFilter === 'monthly' ? 
                    visibleData.reduce((acc, item, index) => {
                      // Show more ticks for monthly view
                      if (index === 0 || index === visibleData.length - 1 || index % 2 === 0) {
                        acc.push(item.timestamp);
                      }
                      return acc;
                    }, []) : undefined}
                />
                <YAxis 
                  label={{ 
                    value: yAxisConfig[selectedMetric].label, 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 0,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666'
                    },
                    dx: -35
                  }}
                  domain={yAxisConfig[selectedMetric].domain}
                  width={80}
                  tickMargin={5}
                  tickFormatter={(value) => value.toFixed(3)}
                />
                <Tooltip
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    switch(timeFilter) {
                      case 'monthly':
                        return date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      case 'weekly':
                        return date.toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      case 'daily':
                        return date.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        });
                      default:
                        return date.toLocaleString();
                    }
                  }}
                  formatter={(value, name) => [
                    `${parseFloat(value).toFixed(3)} °C`,
                    name
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    padding: '8px',
                    borderRadius: '4px'
                  }}
                />
                <Legend />
                {selectedMetric === 'cell_temp' ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="lowest_cell_temp"
                      name="Lowest Cell Temperature"
                      stroke="#FF6384"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="highest_cell_temp"
                      name="Highest Cell Temperature"
                      stroke="#36A2EB"
                      dot={false}
                      strokeWidth={2}
                    />
                  </>
                ) : selectedMetric === 'inverter_temp' ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="inverter_ambient_temp"
                      name="Inverter Ambient"
                      stroke="#FFCE56"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="inverter_heatsink_temp"
                      name="Inverter Heatsink"
                      stroke="#9966FF"
                      dot={false}
                      strokeWidth={2}
                    />
                  </>
                ) : (
                  <Line 
                    type="monotone" 
                    dataKey={selectedMetric}
                    name={temperatureConfigs.find(c => c.value === selectedMetric)?.label}
                    stroke="#8884d8"
                    dot={false}
                    strokeWidth={2}
                  />
                )}
              </LineChart>
            </Box>

            {timeFilter === 'daily' && windowSize && data.length > 0 && (
              <Box sx={{ width: 900, mt: 2, px: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Slide to navigate through data
                </Typography>
                <Slider
                  value={sliderPosition}
                  onChange={handleSliderChange}
                  aria-label="Time window slider"
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={1}
                />
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">No data available for the selected period</Alert>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(TemperatureMetrics);